/**
 * Data retention rules. Pure, so the promises in the privacy policy are
 * unit-tested:
 *
 *  - While an account is active (a live Webflow connection and a trial or
 *    subscription in good standing) its backups are kept, including for
 *    sites switched off.
 *  - Once it goes dormant (the app was removed, or the trial or subscription
 *    ended) everything is deleted RETENTION_DAYS later, with a warning email
 *    WARNING_DAYS before. Coming back before then cancels the deletion.
 *  - A single site the connection can no longer see (moved to another
 *    Workspace, or its access removed) is deleted RETENTION_DAYS after that,
 *    while the rest of the account carries on.
 *  - Raw webhook deliveries are kept WEBHOOK_EVENT_DAYS for debugging.
 */
export const RETENTION_DAYS = 30;
export const WARNING_DAYS = 7;
export const WEBHOOK_EVENT_DAYS = 30;

const DAY = 24 * 60 * 60 * 1000;

export interface AccountRetention {
  /** Webflow connections not revoked */
  liveInstallations: number;
  /** Trial or subscription in good standing (BillingState.entitled) */
  entitled: boolean;
  deleteAfter: Date | null;
  deletionWarningSentAt: Date | null;
  dataDeletedAt: Date | null;
}

export type RetentionStep =
  | { kind: "none" }
  /** Just went dormant: set the deletion date. */
  | { kind: "schedule"; deleteAfter: Date }
  /** Active again: forget any scheduled deletion. */
  | { kind: "clear" }
  /** Deletion is close: email the agency once. */
  | { kind: "warn" }
  /** Deletion date reached. */
  | { kind: "delete" };

export const isDormant = (a: Pick<AccountRetention, "liveInstallations" | "entitled">) =>
  a.liveInstallations === 0 || !a.entitled;

/** Why an account is dormant, for the warning email. */
export const dormantReason = (a: Pick<AccountRetention, "liveInstallations">): "removed" | "unpaid" =>
  a.liveInstallations === 0 ? "removed" : "unpaid";

export function accountRetentionStep(a: AccountRetention, now: Date): RetentionStep {
  if (!isDormant(a)) {
    return a.deleteAfter || a.deletionWarningSentAt || a.dataDeletedAt ? { kind: "clear" } : { kind: "none" };
  }
  if (!a.deleteAfter) {
    // Already deleted and still dormant: nothing left to do.
    if (a.dataDeletedAt) return { kind: "none" };
    return { kind: "schedule", deleteAfter: new Date(now.getTime() + RETENTION_DAYS * DAY) };
  }
  if (now >= a.deleteAfter) return { kind: "delete" };
  if (!a.deletionWarningSentAt && a.deleteAfter.getTime() - now.getTime() <= WARNING_DAYS * DAY) {
    return { kind: "warn" };
  }
  return { kind: "none" };
}

/**
 * Whether one site's data is due for deletion on its own: the connection
 * lost sight of it (or was revoked) at least RETENTION_DAYS ago. Not used
 * for dormant accounts, which follow the account rule and its warning.
 */
export function siteDeletionDue(
  site: { goneAt: Date | null; installationRevokedAt: Date | null },
  now: Date,
): boolean {
  const since = site.goneAt ?? site.installationRevokedAt;
  return !!since && now.getTime() - since.getTime() >= RETENTION_DAYS * DAY;
}

export const webhookEventCutoff = (now: Date) => new Date(now.getTime() - WEBHOOK_EVENT_DAYS * DAY);
